import React from 'react'
import { Printer, Eye, Settings } from 'lucide-react'
import { CupomTermico } from '../services/templates'
import { DadosImpressao, previewCupom, imprimirCupom } from '../utils/impressaoTermica'
import Button from './ui/Button'

interface CupomTermicoPreviewProps {
  cupom: CupomTermico
  dados: DadosImpressao
  onImprimir?: () => void
  onConfigurar?: () => void
}

const CupomTermicoPreview: React.FC<CupomTermicoPreviewProps> = ({
  cupom,
  dados,
  onImprimir,
  onConfigurar
}) => {
  const preview = previewCupom(dados, {
    larguraPapel: cupom.configuracao.larguraPapel,
    tamanhofonte: cupom.configuracao.tamanhoFonte,
    cortarPapel: cupom.configuracao.cortarPapel,
    gaveta: cupom.configuracao.abrirGaveta
  })

  const handleImprimir = async () => {
    try {
      await imprimirCupom(dados, {
        larguraPapel: cupom.configuracao.larguraPapel,
        tamanhofonte: cupom.configuracao.tamanhoFonte,
        cortarPapel: cupom.configuracao.cortarPapel,
        gaveta: cupom.configuracao.abrirGaveta
      })
      onImprimir?.()
    } catch (error) {
      console.error('Erro ao imprimir:', error)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Eye className="h-5 w-5 text-gray-600" />
          <div>
            <h3 className="font-medium text-gray-900">{cupom.nome}</h3>
            <p className="text-sm text-gray-500">
              {cupom.configuracao.larguraPapel}mm • {cupom.configuracao.tamanhoFonte}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onConfigurar && (
            <Button
              variant="outline"
              size="sm"
              onClick={onConfigurar}
              leftIcon={<Settings className="h-4 w-4" />}
            >
              Configurar
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleImprimir}
            leftIcon={<Printer className="h-4 w-4" />}
          >
            Imprimir
          </Button>
        </div>
      </div>

      {/* Preview do cupom */}
      <div className="p-4">
        <div
          className={`
            bg-white border border-gray-300 rounded-lg p-4 font-mono text-xs
            overflow-auto max-h-96 whitespace-pre-line
            ${cupom.configuracao.larguraPapel === 58 ? 'max-w-sm' : 'max-w-lg'}
          `}
          style={{
            fontFamily: '"Courier New", Courier, monospace',
            fontSize: cupom.configuracao.tamanhoFonte === 'pequena' ? '10px' :
                      cupom.configuracao.tamanhoFonte === 'grande' ? '14px' : '12px'
          }}
        >
          {preview}
        </div>
      </div>

      {/* Configurações do cupom */}
      <div className="px-4 pb-4">
        <div className="bg-gray-50 rounded-lg p-3">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Configurações</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Largura:</span>
              <span className="font-medium">{cupom.configuracao.larguraPapel}mm</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Fonte:</span>
              <span className="font-medium capitalize">{cupom.configuracao.tamanhoFonte}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Cortar papel:</span>
              <span className={`font-medium ${cupom.configuracao.cortarPapel ? 'text-green-600' : 'text-gray-400'}`}>
                {cupom.configuracao.cortarPapel ? 'Sim' : 'Não'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Abrir gaveta:</span>
              <span className={`font-medium ${cupom.configuracao.abrirGaveta ? 'text-green-600' : 'text-gray-400'}`}>
                {cupom.configuracao.abrirGaveta ? 'Sim' : 'Não'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Número de vias:</span>
              <span className="font-medium">{cupom.configuracao.numeroVias}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Logo:</span>
              <span className={`font-medium ${cupom.configuracao.incluirLogo ? 'text-green-600' : 'text-gray-400'}`}>
                {cupom.configuracao.incluirLogo ? 'Incluir' : 'Não incluir'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CupomTermicoPreview